import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Facebook } from "react-content-loader";
import { Dispatch } from "redux";
import Hierarchy, { ActionsEnabled, HierarchyItemType } from "./Hierarchy";
import { RootState } from "../../redux/rootReducer";
import { MetricsState, selectors } from "../../logging";
import { LoadingState, useHttpRequest } from "../../http";
import { GetTeamChildrenResponseDto } from "../../common/model/get-team-children.dto";
import { TeamDtoWithBreadcrumb } from "../../layout/subheader/SubHeaderDto";
import { TeamOwnerDetailsResponseDto } from "../../common/model/teamownerdetails.dto";
import { TeamAccessForUserResponseDto } from "../../common/model/team-access-for-user-response.dto";
import { DeleteTeamProps } from "../../deleteTeam/DeleteTeamModal";
import DeleteTeamWrapper from "../../deleteTeam/DeleteTeamWrapper";
import bannerSlice, { DisplayBanner } from "../../banner/bannerSlice";
import subheaderSlice, {
  GetSubheaderDetailFetchPayload,
} from "../../layout/subheader/subheaderSlice";

interface TeamHierarchyProps {
  team: TeamDtoWithBreadcrumb;
}

interface TeamHierarchyStateProps {
  metrics: MetricsState;
}

interface TeamHierarchyDispatchProps {
  setBanner: (banner: DisplayBanner | undefined) => void;
  getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) => void;
}

function getActionsForTeam(
  parentTeamId: string | undefined,
  teamId: string,
  teamActions: Set<string>
): ActionsEnabled {
  return {
    createWorkflowEnabled: teamActions.has(teamId),
    moveWorkflowEnabled: parentTeamId ? teamActions.has(parentTeamId) : false,
    deleteWorkflowEnabled: parentTeamId ? teamActions.has(teamId) : false,
  };
}

function parseUserTeamsAccessResponse(
  teamsAccessForUserResponse: TeamAccessForUserResponseDto
): Set<string> {
  const teamAccess = teamsAccessForUserResponse.data;
  const actionsForUser: Set<string> = new Set(
    teamAccess.map((accessDto) => {
      return accessDto.teamId;
    })
  );

  return actionsForUser;
}

const TeamHierarchy: React.FC<TeamHierarchyProps &
  TeamHierarchyStateProps &
  TeamHierarchyDispatchProps> = ({
  team,
  metrics,
  setBanner,
  getSubheaderDetailFetch,
}): JSX.Element => {
  const parentTeam =
    team.breadcrumbs && team.breadcrumbs.length > 1
      ? team.breadcrumbs[team.breadcrumbs.length - 2]
      : undefined;

  const [deleteTeamProps, setDeleteTeamProps] = useState<DeleteTeamProps>({
    showModal: false,
    teamId: "",
    teamName: "",
    parentTeamId: "",
  });

  const setShowDeleteTeamModal = useCallback(
    (visible: boolean) => {
      setDeleteTeamProps({
        showModal: visible,
        teamId: deleteTeamProps.teamId,
        teamName: deleteTeamProps.teamName,
        parentTeamId: deleteTeamProps.parentTeamId,
      });
    },
    [deleteTeamProps, setDeleteTeamProps]
  );

  const {
    loadingState: teamChildrenLoading,
    data: teamChildren,
  } = useHttpRequest<GetTeamChildrenResponseDto>("getTeamChildren", metrics, {
    url: `/api/v1/teams/${team.teamId}/children`,
    method: "GET",
  });

  const {
    loadingState: teamsOwnersLoading,
    data: teamsOwnersDetails,
    sendRequest: sendGetTeamsOwners,
  } = useHttpRequest<TeamOwnerDetailsResponseDto>("getTeamsOwners", metrics);

  const {
    loadingState: teamsActionsLoading,
    data: teamsAccessForUserResponse,
    sendRequest: sendGetTeamActions,
  } = useHttpRequest<TeamAccessForUserResponseDto>("getTeamsAccess", metrics);

  useEffect(() => {
    if (teamChildrenLoading === LoadingState.RESOLVED) {
      const teamsOwnerIds = new Set(
        teamChildren
          ? teamChildren.children.map((child) =>
              child.owner ? child.owner.employeeId : undefined
            )
          : []
      );
      if (team.owner) {
        teamsOwnerIds.add(team.owner.employeeId);
      }
      if (parentTeam && parentTeam.ownerId) {
        teamsOwnerIds.add(parentTeam.ownerId);
      }
      teamsOwnerIds.delete(undefined);
      sendGetTeamsOwners({
        url: "/api/v1/teams/query/employees",
        method: "POST",
        data: {
          employeeIds: [...teamsOwnerIds],
        },
      });
    }
  }, [
    parentTeam,
    sendGetTeamsOwners,
    team.owner,
    teamChildren,
    teamChildrenLoading,
  ]);

  const ownersMap = new Map<string, string>(
    teamsOwnersDetails
      ? teamsOwnersDetails.data.map((owner) => [
          owner.employeeId,
          owner.employeeFullName,
        ])
      : []
  );

  useEffect(() => {
    if (teamChildrenLoading === LoadingState.RESOLVED) {
      const actionsTeamIds: string[] = [team.teamId];
      if (teamChildren && teamChildren.children) {
        teamChildren.children.forEach((childTeam) =>
          actionsTeamIds.push(childTeam.teamId)
        );
      }
      if (parentTeam && parentTeam.teamId) {
        actionsTeamIds.push(parentTeam.teamId);
      }
      sendGetTeamActions({
        url: "/api/v1/teams/teamAccess/me",
        method: "POST",
        data: {
          teamIds: actionsTeamIds,
        },
      });
    }
  }, [sendGetTeamActions, team, teamChildren, teamChildrenLoading]);

  //TODO: is useMemo() correct here?
  const teamActionsForUser: Set<string> = useMemo(() => {
    if (
      teamsActionsLoading !== LoadingState.RESOLVED ||
      !teamsAccessForUserResponse
    ) {
      return new Set<string>();
    }
    return parseUserTeamsAccessResponse(teamsAccessForUserResponse);
  }, [teamsActionsLoading, teamsAccessForUserResponse]);

  const createOwnerDescription = (ownerId?: string): string => {
    if (!ownerId) {
      return "No owner assigned";
    }
    const ownerName = ownersMap.get(ownerId);
    if (!ownerName) {
      return "No owner assigned";
    }
    return `Owned by ${ownerName}`;
  };

  return teamsOwnersLoading === LoadingState.RESOLVED ? (
    <>
      <Hierarchy
        parent={
          parentTeam
            ? {
                name: parentTeam.name,
                description: createOwnerDescription(parentTeam.ownerId),
                link: `/team/${parentTeam.teamId}`,
                teamId: parentTeam.teamId as string,
                type: HierarchyItemType.PARENT,
              }
            : undefined
        }
        current={{
          name: team.name,
          description: createOwnerDescription(
            team.owner ? team.owner.employeeId : undefined
          ),
          teamId: team.teamId,
          reportsTo: parentTeam ? parentTeam.teamId : undefined,
          actionsEnabled: getActionsForTeam(
            parentTeam && parentTeam.teamId ? parentTeam.teamId : undefined,
            team.teamId,
            teamActionsForUser
          ),
          type: HierarchyItemType.CURRENT,
        }}
        childrenItems={
          teamChildren
            ? teamChildren.children.map((child) => ({
                name: child.name,
                description: createOwnerDescription(
                  child.owner && child.owner !== null
                    ? child.owner.employeeId
                    : undefined
                ),
                link: `/team/${child.teamId}`,
                teamId: child.teamId,
                reportsTo: team.teamId,
                actionsEnabled: getActionsForTeam(
                  team.teamId,
                  child.teamId,
                  teamActionsForUser
                ),
                type: HierarchyItemType.CHILD,
              }))
            : []
        }
        actionsLoading={teamsActionsLoading}
        setDeleteTeamProps={setDeleteTeamProps}
        setBanner={setBanner}
      />
      {deleteTeamProps.showModal && (
        <DeleteTeamWrapper
          {...deleteTeamProps}
          setShowModal={setShowDeleteTeamModal}
          metrics={metrics}
          setBanner={setBanner}
          getSubheaderDetailFetch={getSubheaderDetailFetch}
        />
      )}
    </>
  ) : (
    <Facebook />
  );
};

const mapStateToProperties = (state: RootState): TeamHierarchyStateProps => ({
  metrics: selectors.getMetrics(state),
});

const mapDispatchToProperties = (
  dispatch: Dispatch
): TeamHierarchyDispatchProps => {
  return {
    setBanner: (banner: DisplayBanner | undefined) => {
      dispatch(bannerSlice.actions.setBanner(banner));
    },
    getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) =>
      dispatch(subheaderSlice.actions.getSubheaderDetailFetch(payload)),
  };
};

export default connect(
  mapStateToProperties,
  mapDispatchToProperties
)(TeamHierarchy);
